import { create } from 'zustand';
import ServiceIncidenciaNatureza from '@/services/ServiceIncidenciaNatureza';
import { notify } from '@/stores/useToast';

export interface LinhaIncidenciaNatureza {
  [k: string]: string; // satisfaz o constraint Record<string, unknown> do AgnesDataTable
  codigo: string;
  nome: string;
  situacao: string;
  tipo: string;
  cp: string;
  irrf: string;
  fgts: string;
  cprp: string;
  pisPasep: string;
  teto: string;
  descricao: string;
  inicio: string;
  termino: string;
  alteradoPor: string;
  atualizacao: string;
}

// colunas que viram select no Filtro (espelha a homolog) + Atualização (data).
export const CAMPOS_SELECT = [
  'situacao',
  'tipo',
  'cp',
  'irrf',
  'fgts',
  'cprp',
  'pisPasep',
  'teto',
  'alteradoPor',
] as const;

export type CampoSelect = (typeof CAMPOS_SELECT)[number];

export type FiltrosIncidenciaNatureza = Record<CampoSelect, string> & { atualizacao: string };

// opção de incidência/tipo = código + descrição (contrato da versão antiga). Ver tarefas/CON-5210/legado.md.
export type OpcaoCodigo = { value: string; title: string };

// incidências/tipo = [{value,title}]; situação/teto/atualizado por = [string].
export interface OpcoesFiltro {
  situacao: string[];
  tipo: OpcaoCodigo[];
  cp: OpcaoCodigo[];
  irrf: OpcaoCodigo[];
  fgts: OpcaoCodigo[];
  cprp: OpcaoCodigo[];
  pisPasep: OpcaoCodigo[];
  teto: string[];
  alteradoPor: string[];
}

const COLUNAS_CODIGO = ['tipo', 'cp', 'irrf', 'fgts', 'cprp', 'pisPasep'] as const;

interface State {
  lista: LinhaIncidenciaNatureza[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  busca: string;
  filtros: FiltrosIncidenciaNatureza;
  opcoes: OpcoesFiltro;
  carregando: boolean;
  exportando: boolean;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (p: number) => Promise<void>;
  setSize: (s: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  aplicarFiltros: (f: FiltrosIncidenciaNatureza) => Promise<void>;
  carregarOpcoes: () => Promise<void>;
  exportar: () => Promise<void>;
}

const SEM_FILTRO: FiltrosIncidenciaNatureza = {
  situacao: '',
  tipo: '',
  cp: '',
  irrf: '',
  fgts: '',
  cprp: '',
  pisPasep: '',
  teto: '',
  alteradoPor: '',
  atualizacao: '',
};

const OPCOES_VAZIAS: OpcoesFiltro = {
  situacao: [],
  tipo: [],
  cp: [],
  irrf: [],
  fgts: [],
  cprp: [],
  pisPasep: [],
  teto: ['Não', 'Sim'],
  alteradoPor: [],
};

// term + só os filtros preenchidos (o backend faz match exato por coluna; Atualização = data dd/mm/aaaa).
function payload(busca: string, filtros: FiltrosIncidenciaNatureza) {
  const ativos = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
  return { term: busca, ...ativos };
}

export const useIncidenciaNaturezaStore = create<State>((set, get) => ({
  lista: [],
  total: 0,
  page: 0,
  size: 50,
  sortBy: '',
  sortOrder: 'asc',
  busca: '',
  filtros: SEM_FILTRO,
  opcoes: OPCOES_VAZIAS,
  carregando: false,
  exportando: false,

  setBusca(v) {
    set({ busca: v });
  },

  async buscar() {
    const { busca, filtros, page, size, sortBy, sortOrder } = get();
    set({ carregando: true });
    try {
      const sort = sortBy ? `${sortBy},${sortOrder}` : undefined;
      const { data } = await ServiceIncidenciaNatureza.search(payload(busca, filtros), {
        page,
        size,
        sort,
      });
      set({ lista: data.content ?? [], total: data.totalElements ?? 0 });
    } catch {
      set({ lista: [], total: 0 });
      notify.error('Erro ao carregar as incidências por natureza.');
    } finally {
      set({ carregando: false });
    }
  },

  async setPage(p) {
    set({ page: p });
    await get().buscar();
  },
  async setSize(s) {
    set({ size: s, page: 0 });
    await get().buscar();
  },
  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().buscar();
  },

  async aplicarFiltros(f) {
    set({ filtros: f, page: 0 });
    await get().buscar();
  },

  async carregarOpcoes() {
    try {
      const { data } = await ServiceIncidenciaNatureza.filters();
      const op: OpcoesFiltro = { ...OPCOES_VAZIAS };
      // strings
      if (Array.isArray(data?.situacao)) op.situacao = data.situacao;
      if (Array.isArray(data?.teto)) op.teto = data.teto;
      if (Array.isArray(data?.alteradoPor)) op.alteradoPor = data.alteradoPor;
      // {value,title}
      for (const c of COLUNAS_CODIGO) if (Array.isArray(data?.[c])) op[c] = data[c];
      set({ opcoes: op });
    } catch {
      set({ opcoes: OPCOES_VAZIAS });
    }
  },

  async exportar() {
    set({ exportando: true });
    try {
      const { busca, filtros } = get();
      const { data } = await ServiceIncidenciaNatureza.exportData(payload(busca, filtros));
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'incidencia-por-natureza.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify.error('Não foi possível exportar.');
    } finally {
      set({ exportando: false });
    }
  },
}));
